import React, { useEffect } from 'react'
import className from 'classnames/bind'
import { useHistory } from 'react-router'
import { debounce } from 'lodash'

import style from './MyhouseContextMenu.module.scss'

import { MY_HOUSE_BOARD_TYPE } from '@Root/contants/myhouse'
import useNotice from '@Hooks/myhouse/notice/useNotice'
import { LAND_SERVICE_PREFIX_URL } from '@Root/contants/urlConstants'
import useMyhouse from '@Hooks/myhouse/useMyhouse'
import useReview from '@Hooks/myhouse/review/useReview'
import useModal from '@Hooks/myhouse/useModal'
import { MODAL_TYPE } from '@Root/contants/myhouseModal'
import useBoardRegister from '@Hooks/myhouse/board/useBoardRegister'
import useDebounce from '@Hooks/common/useDebounce'
import { FETCH_STATUS } from '@Root/contants'
import useToast from '@Hooks/myhouse/useToast'
import { TOAST_AFTER_EVENT, TOAST_TEXT } from '@Root/contants/myhouseToast'
import useBoard from '@Hooks/myhouse/board/useBoard'
import { landAxios } from '@Root/utils/landAxios'
import { handleNclick } from '@Helpers/nclick'
import { NCLICK } from '@Root/contants/lcs'

const cx = className.bind(style)

type Props = {
  isWriter: boolean
  boardType: MY_HOUSE_BOARD_TYPE
}

const MyhouseContextMenu: React.FC<Props> = ({ isWriter, boardType }: Props) => {
  const history = useHistory()
  const { landDebounce } = useDebounce()

  const { myhouse } = useMyhouse()
  const complexNo = myhouse?.complexNo

  const { notice } = useNotice()
  const noticeSequence = notice?.detail?.sequence

  const { review } = useReview()
  const reviewSequence = review?.detail?.sequence

  const { getRegistrableComplexNo } = useBoard()

  const { reduxBoardRegister, loadBoardDelete, initFetchStatus } = useBoardRegister()
  const debouncedLoadBoardDelete = landDebounce(loadBoardDelete)

  const { fetchStatus } = reduxBoardRegister

  const { show } = useToast()

  const { open, modal, setConfirmFlag } = useModal()
  const { confirmFlag } = modal

  // todo : 스타일 top

  useEffect(() => {
    setConfirmFlag(false)
    initFetchStatus()
  }, [])

  useEffect(() => {
    if (fetchStatus === FETCH_STATUS.SUCCESS) {
      if (document.referrer && document.referrer.includes('/myhouse') && !document.referrer.includes('/complexes')) {
        show(TOAST_TEXT.ARTICLE_DELETED, TOAST_AFTER_EVENT.LOCATION_HREF_REFERRER)
      } else if (document.referrer && document.referrer.includes('m.land.naver.com')) {
        show(TOAST_TEXT.ARTICLE_DELETED, TOAST_AFTER_EVENT.LOCATION_HREF, `${process.env.REACT_APP_SPACE_WEB_URL}/complex/info/${complexNo}`)
      } else {
        if (boardType === MY_HOUSE_BOARD_TYPE.NOTICE) {
          show(TOAST_TEXT.ARTICLE_DELETED, TOAST_AFTER_EVENT.HISTORY_PUSH, `/myhouse/complexes/${complexNo}/notices`)
        } else {
          show(TOAST_TEXT.ARTICLE_DELETED, TOAST_AFTER_EVENT.HISTORY_PUSH, `/myhouse/complexes/${complexNo}/reviews`)
        }
      }

      setConfirmFlag(false)
      initFetchStatus()
    }
  }, [fetchStatus])

  useEffect(() => {
    if (modal.confirmFlag) {
      const sequence = boardType === MY_HOUSE_BOARD_TYPE.NOTICE ? noticeSequence : reviewSequence
      debouncedLoadBoardDelete({ sequence })
    }
  }, [confirmFlag])

  const modifyHandler = (evt: React.SyntheticEvent) => {
    evt.preventDefault()
    evt.stopPropagation()

    handleNclick(boardType === MY_HOUSE_BOARD_TYPE.NOTICE ? NCLICK.MY_HOUSE.NOTICE_DETAIL.EDIT : NCLICK.MY_HOUSE.REVIEW_DETAIL.EDIT, evt)

    // 작성 이후 인증이 풀린 경우
    if (!getRegistrableComplexNo(complexNo)) {
      open(MODAL_TYPE.ARTICLE_AUTH_REQUIRED)
      return
    }

    if (boardType === MY_HOUSE_BOARD_TYPE.NOTICE) {
      history.push(`${LAND_SERVICE_PREFIX_URL.MYHOUSE_COMPLEXES}/${complexNo}/board/form/notices/${noticeSequence}`)
      return
    }
    history.push(`${LAND_SERVICE_PREFIX_URL.MYHOUSE_COMPLEXES}/${complexNo}/board/form/reviews/${reviewSequence}`)
  }

  const deleteHandler = (evt: React.SyntheticEvent) => {
    evt.preventDefault()
    evt.stopPropagation()

    handleNclick(boardType === MY_HOUSE_BOARD_TYPE.NOTICE ? NCLICK.MY_HOUSE.NOTICE_DETAIL.DELETE : NCLICK.MY_HOUSE.REVIEW_DETAIL.DELETE, evt)

    if (fetchStatus === FETCH_STATUS.PENDING) {
      return
    }

    open(MODAL_TYPE.DELETE_ARTICLE, {
      nclickConfirm: boardType === MY_HOUSE_BOARD_TYPE.NOTICE ? NCLICK.MY_HOUSE.NOTICE_DETAIL.DELETE_POPUP : NCLICK.MY_HOUSE.REVIEW_DETAIL.DELETE_POPUP,
    })
  }

  const reportContent = () => {
    let url

    /**
     * iOS Safari 대응.
     * iOS는 사용자 이벤트를 통한 함수호출에서의 Promise 이후의 처리에서 window.open을 허용하지 않습니다.
     */
    const reportPopupOpen = window.open('', 'SRP', 'height=800,width=580,scrollbars=yes,menubar=no,toolbar=no,location=no')

    if (boardType === MY_HOUSE_BOARD_TYPE.NOTICE) {
      url = `/myhouse/complexes/${complexNo}/report/notices/${noticeSequence}`
    } else {
      url = `/myhouse/complexes/${complexNo}/report/reviews/${reviewSequence}`
    }
    landAxios
      .get(url)
      .then((res) => {
        if (reportPopupOpen) {
          reportPopupOpen.location = res.data
        } else {
          /**
           * Android LandWeb 대응.
           * PopupActivity의 WebView 동작으로 인해 에서 이동처리로 동작해 빈 문자열의 reportPopupOpen가 비어있습니다.
           */
          window.open(res.data, 'SRP', 'height=800,width=580,scrollbars=yes,menubar=no,toolbar=no,location=no')
        }
      })
      .catch((e) => {
        console.log(e)
      })
  }
  const reportContentDebounced = debounce(reportContent, 300)

  const reportHandler = (evt: React.SyntheticEvent) => {
    evt.preventDefault()
    evt.stopPropagation()

    handleNclick(boardType === MY_HOUSE_BOARD_TYPE.NOTICE ? NCLICK.MY_HOUSE.NOTICE_DETAIL.REPORT : NCLICK.MY_HOUSE.REVIEW_DETAIL.REPORT, evt)

    reportContentDebounced()
  }

  return (
    <div className={cx('myhouse_context_menu', boardType === MY_HOUSE_BOARD_TYPE.NOTICE && 'type_notice')}>
      {isWriter ? (
        <>
          <button type="button" className={cx('myhouse_button', 'type_modify')} onClick={modifyHandler}>
            수정하기
          </button>
          <button type="button" className={cx('myhouse_button', 'type_delete')} onClick={deleteHandler}>
            삭제하기
          </button>
        </>
      ) : (
        <button type="button" className={cx('myhouse_button', 'type_report')} onClick={reportHandler}>
          신고하기
        </button>
      )}
    </div>
  )
}

export default MyhouseContextMenu
